export const DEFAULT_THRESHOLDS = {
  lcp: { good: 2500, poor: 4000 },
  fcp: { good: 1800, poor: 3000 },
  cls: { good: 0.1, poor: 0.25 },
  fid: { good: 100, poor: 300 },
  ttfb: { good: 800, poor: 1800 },
};

const TYPE_NORM = {
  stylesheet: 'css',
  script: 'script',
  image: 'image',
  font: 'font',
  xhr: 'xhr',
  fetch: 'xhr',
  document: 'doc',
};

export function extractEntry(result) {
  if (!result?.meta) return null;

  let totalRequests = 0;
  let failedRequests = 0;
  let totalSize = 0;
  let thirdPartyCount = 0;
  let thirdPartyLoadTime = 0;
  let totalResourceTime = 0;
  const resourceCounts = {};
  const resourceSizes = {};

  try {
    const resources = JSON.parse(result.response?.resource_requests || '[]');
    let pageDomain = null;
    try {
      pageDomain = new URL(result.request?.url || '').hostname;
    } catch {
      // URL unavailable — skip 3rd party detection
    }

    totalRequests = resources.length;
    failedRequests = resources.filter((r) => {
      const status = parseInt(r.status || r.response?.status, 10);
      return status >= 400;
    }).length;

    resources.forEach((r) => {
      const size = r.size ?? r.timing?.encodedBodySize ?? 0;
      totalSize += size > 0 ? size : 0;

      const rawType = (r.resource_type || r.type || '').toLowerCase();
      const type = TYPE_NORM[rawType] || rawType || 'other';
      resourceCounts[type] = (resourceCounts[type] || 0) + 1;
      resourceSizes[type] = (resourceSizes[type] || 0) + (size > 0 ? size : 0);

      const dur =
        r.duration ??
        r.timing?.duration ??
        (r.timing?.responseEnd != null && r.timing?.startTime != null
          ? r.timing.responseEnd - r.timing.startTime
          : 0);
      const durMs = dur > 0 ? dur : 0;
      totalResourceTime += durMs;

      if (pageDomain) {
        let isThirdParty = false;
        try {
          isThirdParty = new URL(r.url).hostname !== pageDomain;
        } catch {
          // unparseable URL — treat as 1st party
        }
        if (isThirdParty) {
          thirdPartyCount += 1;
          thirdPartyLoadTime += durMs;
        }
      }
    });
  } catch {
    // resource_requests absent or malformed — leave counts at 0
  }

  return {
    resultId: result.id,
    callId: result.meta.call_id,
    timestamp: result.meta.start_time,
    resultCategory: result.meta.result_category,
    location: result.meta.location_id,
    browserType: result.request?.browser_type || null,
    lcp: result.response?.largest_contentful_paint ?? null,
    fcp: result.response?.first_contentful_paint ?? null,
    cls: result.response?.cumulative_layout_shift ?? null,
    fid: result.response?.first_input_delay ?? null,
    ttfb: result.timing?.processing ?? null,
    pageLoadTime: result.timing?.total ?? null,
    totalRequests,
    failedRequests,
    totalSize,
    thirdPartyCount,
    thirdPartyLoadTime,
    totalResourceTime,
    resourceCounts,
    resourceSizes,
  };
}

export function fmtMs(val) {
  return val !== null && val !== undefined ? `${Math.round(val)}ms` : '—';
}

export function fmtBytes(val) {
  if (val === null || val === undefined || val === 0) return '—';
  if (val < 1024) return `${Math.round(val)} B`;
  if (val < 1024 * 1024) return `${(val / 1024).toFixed(1)} KB`;
  return `${(val / (1024 * 1024)).toFixed(2)} MB`;
}

export function rateMetric(metric, value, thresholds = null) {
  if (value === null || value === undefined) return 'unknown';
  const t = thresholds || DEFAULT_THRESHOLDS;
  const bounds = t[metric];
  if (!bounds) return 'unknown';
  if (value <= bounds.good) return 'good';
  if (value <= bounds.poor) return 'needs-improvement';
  return 'poor';
}
